/**
 * KanbanEmptyState - Estado vazio do Kanban
 * 
 * Exibido quando não há categorias criadas
 */

import { useState } from 'react';
import { Kanban, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

// Cores pré-definidas
const PRESET_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
];

interface KanbanEmptyStateProps {
  onCreateCategory: (name: string, color: string) => Promise<any>;
}

export function KanbanEmptyState({ onCreateCategory }: KanbanEmptyStateProps) {
  const [showDialog, setShowDialog] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    await onCreateCategory(name.trim(), color);
    setShowDialog(false);
    setName('');
    setColor(PRESET_COLORS[0]);
  };

  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-200px)] text-center">
      <div className="p-4 rounded-full bg-primary/10 mb-4">
        <Kanban className="h-12 w-12 text-primary" />
      </div>
      
      <h2 className="text-xl font-semibold mb-2">
        Organize suas conversas com Kanban
      </h2>
      
      <p className="text-muted-foreground max-w-md mb-6">
        Crie categorias (como Vendas, Suporte) e fases (como Novo Lead, Em Contato, Fechado)
        para acompanhar visualmente o progresso das suas conversas.
      </p>
      
      <Button onClick={() => setShowDialog(true)}>
        <Plus className="h-4 w-4 mr-2" />
        Criar Primeira Categoria
      </Button>

      {/* Dialog de Criação */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Categoria</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome da Categoria</Label>
              <Input
                placeholder="Ex: Vendas, Suporte, Pós-venda..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2">
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={cn(
                      'w-8 h-8 rounded-full transition-all',
                      color === c && 'ring-2 ring-offset-2 ring-primary'
                    )}
                    style={{ backgroundColor: c }}
                    onClick={() => setColor(c)}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={!name.trim()}>
              Criar Categoria
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
